import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { requestLogin, requestRefresh } from '../asyncHelpers/authService';
import { requestUserConfig } from '../asyncHelpers/configService';
import AuthRequestStatus from '../enums/AuthRequestStatus';
import { USER_CONFIG_INITIAL_STATE } from '../sic-lib/defaults';
import { PackageFeatures } from '../sic-lib/enums/Billing';
import { RequestStatus } from '../sic-lib/enums/RequestStatus';
import { UpdatePasswordInputs } from '../sic-lib/types/Password';
import { UserConfig, mapUserConfigResponseToUserConfig } from '../sic-lib/types/UserConfig';
import { userRoles } from '../sic-lib/types/UserRoles';
import { hasPackageFeature } from '../sic-lib/utils/billingHelpers';
import { isUserInRole } from '../sic-lib/utils/userHelpers';
import { createParametricSelectorHook } from './hooks';
import { RootState } from './store';

// --------------
// STATE

interface AuthStatus {
    hasReturningAuth: boolean;
    requestStatus: AuthRequestStatus;
    error: string | null;
}

export interface UserState extends UserConfig {
    requestStatus: RequestStatus;
    authStatus: AuthStatus;
}

const initialState: UserState = {
    authStatus: {
        hasReturningAuth: false,
        requestStatus: AuthRequestStatus.none,
        error: null,
    },
    requestStatus: RequestStatus.none,
    ...USER_CONFIG_INITIAL_STATE,
};

const usersSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        updateRequestStatus: (state, action: PayloadAction<AuthRequestStatus>) => {
            state.authStatus.requestStatus = action.payload;
        },
        clearAuth: state => {
            state.authStatus.requestStatus = AuthRequestStatus.error;
        },
        updateTimeZone: (state, action: PayloadAction<string>) => {
            state.timeZoneId = action.payload;
        },
        updateUserPassword: (state, action: PayloadAction<UpdatePasswordInputs>) => {
            // no-op
        },
    },
    extraReducers: builder => {
        builder
            .addCase(requestVerifyAuth.pending, (state, action) => {
                state.authStatus.requestStatus = AuthRequestStatus.verify;
            })
            .addCase(requestVerifyAuth.fulfilled, (state, action) => {
                state.authStatus.requestStatus = AuthRequestStatus.authSuccess;
                state.authStatus.hasReturningAuth = true;
            })
            .addCase(requestVerifyAuth.rejected, (state, action) => {
                state.authStatus.requestStatus = AuthRequestStatus.await;
                state.authStatus.hasReturningAuth = false;
            });

        builder
            .addCase(requestToken.pending, (state, action) => {
                state.authStatus.requestStatus = AuthRequestStatus.active;
            })
            .addCase(requestToken.fulfilled, (state, action) => {
                state.authStatus.requestStatus = AuthRequestStatus.authSuccess;
            })
            .addCase(requestToken.rejected, (state, action) => {
                state.authStatus.requestStatus = AuthRequestStatus.error;
                state.authStatus.error = action?.payload?.toString() || 'Unknown server error.';
            });

        builder
            .addCase(requestConfig.pending, (state, action) => {
                state.requestStatus = RequestStatus.active;
            })
            .addCase(requestConfig.fulfilled, (state, action) => {
                return {
                    ...state,
                    authStatus: {
                        ...state.authStatus,
                        requestStatus: AuthRequestStatus.configSuccess,
                    },
                    ...mapUserConfigResponseToUserConfig(action.payload),
                };
            })
            .addCase(requestConfig.rejected, (state, action) => {
                state.authStatus.requestStatus = AuthRequestStatus.error;
                state.authStatus.error = action.error.message ?? null;
            });

        builder
            .addCase(updateUserInboxFilter.pending, (state, action) => {
                state.requestStatus = RequestStatus.active;
            })
            .addCase(updateUserInboxFilter.fulfilled, (state, action) => {
                state.requestStatus = RequestStatus.success;
                state.inboxFilter = action.payload.inboxFilter;
            })
            .addCase(updateUserInboxFilter.rejected, (state, action) => {
                state.requestStatus = RequestStatus.error;
            });
    },
});

export const { updateRequestStatus, clearAuth, updateTimeZone, updateUserPassword } =
    usersSlice.actions;

export default usersSlice.reducer;

// ---------------
// THUNKS

export const requestConfig = createAsyncThunk('user/requestConfig', async () => {
    const response = await requestUserConfig();
    return response.data;
});

export const requestVerifyAuth = createAsyncThunk('user/requestVerifyAuth', async () => {
    await requestRefresh();
});

export const requestToken = createAsyncThunk<
    { access_token: string },
    { username: string; password: string }
>('user/requestToken', async ({ username, password }, thunkApi) => {
    try {
        const response = await requestLogin(username, password);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error)) {
            return thunkApi.rejectWithValue(error?.response?.data.toString() || error.message);
        }
        return thunkApi.rejectWithValue('Request Failed. Please try again.');
    }
});

export const updateUserInboxFilter = createAsyncThunk('user/updateUserInboxFilter', async () => {
    const response = await requestUserConfig();
    return response.data;
});

//reusable selectors wrapped in hooks to avoid lots of repetitive error-prone code

export const useUserName = createParametricSelectorHook((state: RootState) => state.user.userName);
export const useUserInfo = createParametricSelectorHook(
    createSelector([(state: RootState) => state.user], user =>
        user.users.find(u => u.userName === user.userName),
    ),
);

export const useUserHasPackageFeatureAi = createParametricSelectorHook(
    createSelector([(state: RootState) => state.user], user =>
        hasPackageFeature(user.billingPackage, PackageFeatures.Ai),
    ),
);

export const useUserHasPackageFeatureActionPlans = createParametricSelectorHook(
    createSelector([(state: RootState) => state.user], user =>
        hasPackageFeature(user.billingPackage, PackageFeatures.ActionsPlans),
    ),
);

export const useUserHasDialerSetup = createParametricSelectorHook(
    createSelector(
        [(state: RootState) => state.user],
        user => user.dialerIsEnabled && user.dialerIsSetup && user.dialerIsSic,
    ),
);

export const useUserHasDialer = createParametricSelectorHook(
    createSelector(
        [(state: RootState) => state.user],
        user => user.dialerIsEnabled && user.dialerIsSic,
    ),
);

export const useUserIsAdmin = createParametricSelectorHook(
    createSelector([(state: RootState) => state.user], user =>
        isUserInRole(user.userRoles, userRoles.admins),
    ),
);

export const useUserIsSupport = createParametricSelectorHook(
    createSelector([(state: RootState) => state.user], user =>
        isUserInRole(user.userRoles, userRoles.support),
    ),
);

export const useUserIsTechSupport = createParametricSelectorHook(
    createSelector([(state: RootState) => state.user], user =>
        isUserInRole(user.userRoles, userRoles.techSupport),
    ),
);
